import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import SyncResultView from './SyncResultView';

const textToKeys = (text) =>
  text
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);

/** True when applying this plan would write nothing, anywhere. */
const planIsEmpty = (plan) => {
  if (!plan) return false;
  const localCount = ((plan.local && plan.local.created) || []).length;
  const peerCount = (plan.peers || []).reduce(
    (total, peer) => total + (((peer.result || {}).created || []).length),
    0
  );
  return localCount === 0 && peerCount === 0;
};

/**
 * Reconciles the code catalog against the database, in two steps.
 *
 * The endpoint defaults to a dry run because it is built to be previewed: it returns the
 * whole plan per environment, and a single-click apply would throw away the one preview the
 * system offers. So Preview is a separate, unavoidable step, and editing anything after a
 * preview discards it — otherwise you would be applying a plan you did not read.
 *
 * The operation is idempotent, so re-running after a partial failure is the fix, not a risk.
 */
const SyncFlagsModal = ({ open, onSync, onApplied, onClose }) => {
  const [keysText, setKeysText] = useState('');
  const [propagate, setPropagate] = useState(false);
  const [reason, setReason] = useState('');
  const [plan, setPlan] = useState(null);
  const [applied, setApplied] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setKeysText('');
    setPropagate(false);
    setReason('');
    setPlan(null);
    setApplied(null);
    setError(null);
  }, [open]);

  const keys = textToKeys(keysText);

  // Any change to what would be written invalidates the plan on screen. Keeping a stale
  // preview next to an Apply button is how someone propagates to three environments having
  // previewed one.
  const editInputs = (apply) => {
    setPlan(null);
    setError(null);
    apply();
  };

  const run = (dryRun) => {
    setBusy(true);
    setError(null);
    onSync({ keys, dryRun, propagate, reason })
      .then((response) => {
        if (dryRun) {
          setPlan(response);
          return;
        }
        setApplied(response);
        onApplied(response);
      })
      .catch((err) =>
        setError((err && err.message) || 'Could not reach the sync endpoint')
      )
      .finally(() => setBusy(false));
  };

  const nothingToApply = planIsEmpty(plan);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Sync catalog
        <Typography variant="body2" color="textSecondary">
          Creates rows for flags the code declares but the database is missing. Preview first,
          then apply.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Box mb={2}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {applied ? (
          <>
            <Box mb={2}>
              <Alert severity="success">
                Applied. Every row created here starts invisible — releasing is a separate
                action, taken per environment.
              </Alert>
            </Box>
            <SyncResultView result={applied} />
          </>
        ) : (
          <>
            <TextField
              fullWidth
              multiline
              minRows={2}
              variant="outlined"
              label="Keys (one per line — leave empty for the whole catalog)"
              value={keysText}
              onChange={(e) => editInputs(() => setKeysText(e.target.value))}
              inputProps={{ style: { fontFamily: 'monospace' } }}
            />

            <Box mt={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={propagate}
                    onChange={(e) => editInputs(() => setPropagate(e.target.checked))}
                    color="primary"
                  />
                }
                label="Reconcile the other environments too"
              />
              <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
                Rows are created everywhere; release state is not carried anywhere. Each
                environment decides its own, so production rows arrive OFF.
              </Typography>
            </Box>

            <Box mt={2}>
              <TextField
                fullWidth
                variant="outlined"
                label="Reason (recorded in the release log)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Box>

            {plan && (
              <Box mt={2}>
                <Box mb={1}>
                  <Alert severity="info">
                    Preview only — nothing has been written. This is what Apply would do.
                  </Alert>
                </Box>
                <SyncResultView result={plan} />
                {nothingToApply && (
                  <Alert severity="success">
                    Everything the catalog declares already has a row. Nothing to apply.
                  </Alert>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{applied ? 'Done' : 'Cancel'}</Button>
        {!applied && (
          <>
            <Button onClick={() => run(true)} disabled={busy}>
              {busy && !plan ? 'Reading…' : 'Preview'}
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={() => run(false)}
              disabled={busy || !plan || nothingToApply}>
              {busy ? 'Applying…' : 'Apply plan'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SyncFlagsModal;
